import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Loader2, BadgeCheck, MessageSquarePlus, Send } from "lucide-react";
import api, { apiErrorMessage } from "../services/api";
import { useAuth } from "../context/AuthContext";

const Stars = ({ value, size = 16, onChange, hover, setHover }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= (hover || value);
      return onChange ? (
        <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => onChange(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} className="p-0.5 transition-transform hover:scale-110">
          <Star size={22} className={filled ? "fill-[#f5a623] text-[#f5a623]" : "text-slate-600"} />
        </button>
      ) : (
        <Star key={n} size={size} className={filled ? "fill-[#f5a623] text-[#f5a623]" : "text-slate-700"} />
      );
    })}
  </div>
);

const ReviewForm = ({ productId, onCreated }) => {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSending(true);
    try {
      const { data } = await api.post(`/products/${productId}/reviews`, { rating, title, comment });
      onCreated(data);
      setTitle(""); setComment("");
    } catch (err) {
      setError(apiErrorMessage(err, "Impossible de publier votre avis."));
    } finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} data-testid="review-form" className="bg-[#10131A] border border-[#1E232D] rounded-2xl p-5 md:p-6 space-y-4">
      <div className="flex items-center gap-2 font-bold text-slate-50"><MessageSquarePlus size={18} className="text-[#FF3B3B]" /> Donner mon avis</div>
      <div>
        <label className="block text-[13px] font-semibold text-slate-300 mb-1.5">Votre note</label>
        <Stars value={rating} onChange={setRating} hover={hover} setHover={setHover} />
      </div>
      <input data-testid="review-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Titre (optionnel)" className="w-full border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F] text-slate-50 text-[14px]" />
      <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={1500} placeholder="Qualité, montage, compatibilité, livraison… (10 caractères minimum)" className="w-full border-2 border-[#222735] rounded-lg px-3 py-2.5 outline-none focus:border-[#E01F1F] text-slate-50 text-[14px] resize-y" />
      {error && <p data-testid="review-error" className="text-red-400 text-[13px] font-medium">{error}</p>}
      <button data-testid="review-submit" type="submit" disabled={sending || comment.trim().length < 10} className="btn-orange rounded-lg px-6 py-2.5 font-bold flex items-center gap-2 disabled:opacity-50">
        {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Publier mon avis
      </button>
    </form>
  );
};

export const ProductReviews = ({ product, onChange }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [justPosted, setJustPosted] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${product.id}/reviews`).then(({ data }) => setReviews(data)).catch(() => setReviews([])).finally(() => setLoading(false));
  }, [product.id]);

  const mine = user && reviews.some((r) => r.user_name === user.name && r.product_id === product.id);
  const dist = [5, 4, 3, 2, 1].map((n) => ({ n, c: reviews.filter((r) => r.rating === n).length }));

  return (
    <section data-testid="product-reviews" className="mt-16">
      <div className="flex items-end justify-between mb-6">
        <h2 className="speed-line font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl md:text-3xl">Avis clients</h2>
        <div className="flex items-center gap-2">
          <Stars value={Math.round(product.rating)} />
          <span className="text-slate-300 font-bold">{product.reviews > 0 ? product.rating : "—"}</span>
          <span className="text-slate-500 text-sm">({product.reviews} avis)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-[#10131A] border border-[#1E232D] rounded-2xl p-5">
            <div className="flex items-end gap-2 mb-4">
              <span className="font-display font-extrabold italic text-5xl text-white leading-none">{product.reviews > 0 ? product.rating : "—"}</span>
              <span className="text-slate-500 mb-1">/ 5</span>
            </div>
            <div className="space-y-1.5">
              {dist.map(({ n, c }) => (
                <div key={n} className="flex items-center gap-2 text-[12px] text-slate-400">
                  <span className="w-3">{n}</span><Star size={11} className="fill-[#f5a623] text-[#f5a623]" />
                  <div className="flex-1 h-1.5 rounded-full bg-[#181C26] overflow-hidden"><div className="h-full bg-[#E01F1F]" style={{ width: reviews.length ? `${(c / reviews.length) * 100}%` : 0 }} /></div>
                  <span className="w-5 text-right">{c}</span>
                </div>
              ))}
            </div>
          </div>
          {user ? (
            mine || justPosted ? (
              <div data-testid="review-already" className="bg-emerald-950/40 border border-emerald-800/50 text-emerald-300 rounded-2xl p-4 text-[14px] flex items-center gap-2"><BadgeCheck size={18} /> Merci, votre avis a bien été publié.</div>
            ) : (
              <ReviewForm productId={product.id} onCreated={(r) => { setReviews((l) => [r, ...l]); setJustPosted(true); onChange?.(); }} />
            )
          ) : (
            <div className="bg-[#10131A] border border-[#1E232D] rounded-2xl p-5 text-[14px] text-slate-400">
              <Link to="/connexion" data-testid="review-login-link" className="text-[#FF3B3B] font-bold hover:underline">Connectez-vous</Link> à votre compte client pour donner votre avis.
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-3">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#FF3B3B]" size={28} /></div>
          ) : reviews.length === 0 ? (
            <div data-testid="reviews-empty" className="bg-[#10131A] border border-[#1E232D] rounded-2xl p-10 text-center text-slate-400">Aucun avis client pour le moment. Soyez le premier à partager votre expérience.</div>
          ) : reviews.map((r) => (
            <article key={r.id} data-testid={`review-${r.id}`} className="bg-[#10131A] border border-[#1E232D] rounded-2xl p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#E01F1F]/15 border border-[#E01F1F]/30 flex items-center justify-center font-display font-bold text-[#FF3B3B]">{(r.user_name || "C")[0].toUpperCase()}</div>
                  <div>
                    <div className="text-white font-bold text-sm flex items-center gap-1.5">{r.user_name}{r.verified && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-950/70 border border-emerald-800/60 rounded px-1.5 py-0.5"><BadgeCheck size={11} /> Achat vérifié</span>}</div>
                    <div className="text-slate-500 text-[12px]">{new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                </div>
                <Stars value={r.rating} size={14} />
              </div>
              {r.title && <div className="font-semibold text-slate-100 mb-1">{r.title}</div>}
              <p className="text-slate-300 text-[14px] leading-relaxed">{r.comment}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductReviews;
